import { useMemo } from 'react';

import type {
	ImportImageUrlFn,
	UploadImageFn,
} from '../../components/RichTextEditor';
import {
	useImportProgramImageMutation,
	useUploadProgramImageMutation,
} from '../../redux/api/manufacturing';

/**
 * The pair of image handlers every program rich-text field needs, ready to
 * spread onto a `RichTextEditor`.
 *
 * Two handlers because a paste arrives in one of two shapes: the clipboard
 * carries the bytes (upload them), or it carries only markup pointing at
 * someone else's host (hand the URL to the server, which can fetch what the
 * browser is not allowed to).
 *
 * Both are optional: an editor given neither stays text-only, which is what a
 * field with no program to attach an image to has to fall back on.
 */
export interface ProgramImageHandlers {
	onUploadImage?: UploadImageFn;
	onImportImageUrl?: ImportImageUrlFn;
}

export function useProgramImages(
	programId: string | null | undefined
): ProgramImageHandlers {
	const [uploadImage] = useUploadProgramImageMutation();
	const [importImage] = useImportProgramImageMutation();

	return useMemo(() => {
		if (!programId) {
			return {};
		}

		return {
			onUploadImage: (file: File) => uploadImage({ programId, file }).unwrap(),
			onImportImageUrl: (url: string) =>
				importImage({ programId, url }).unwrap(),
		};
	}, [programId, uploadImage, importImage]);
}
